/**
 * useVoiceRecorder — MediaRecorder lifecycle for the composer's microphone.
 *
 * Owns ONLY the recording: permission, codec choice, elapsed time, an input
 * level for the meter, and producing a Blob. It does not know the API exists
 * — `useTranscribe` takes the Blob from here. Keeping the two apart is what
 * lets the recorder be tested without a server and the transcription be
 * tested without a microphone.
 *
 * TAP TO START, TAP TO STOP — not hold-to-talk. Press-and-hold is hostile to
 * motor impairment and switch control, fights the browser's own long-press
 * (context menu, text selection) on mobile, and is simply unpleasant to hold
 * for a minute. A toggle with a visible timer costs one extra tap and works
 * for everyone.
 *
 * NO SINGLE CODEC WORKS EVERYWHERE. Chrome and Android record
 * `audio/webm;codecs=opus`; Safari and iOS record `audio/mp4` (AAC). The
 * preference list below is probed with `isTypeSupported` and the FIRST
 * supported entry wins, so iOS gets a working microphone instead of a
 * silently broken one.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Recorder lifecycle. `denied` and `unsupported` are terminal per mount. */
export type RecorderState =
  | "idle"
  | "requesting"
  | "recording"
  | "denied"
  | "unsupported";

/**
 * Candidate containers, most-preferred first. Opus is first because it is
 * dramatically smaller per second of speech — this is a mobile-first product
 * on Kyrgyz networks, and the clip has to leave the phone.
 */
const MIME_PREFERENCES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
] as const;

/** How often the timer and level meter refresh. */
const TICK_MS = 100;

export interface UseVoiceRecorderOptions {
  /** Hard stop, in ms. At the cap we STOP and transcribe — never discard. */
  maxDurationMs: number;
  /** Called with the finished clip when recording stops normally. */
  onComplete: (blob: Blob, mimeType: string, durationMs: number) => void;
}

export interface UseVoiceRecorderResult {
  state: RecorderState;
  /** Elapsed recording time in ms (0 when not recording). */
  elapsedMs: number;
  /** Smoothed input level in [0,1] for the meter. 0 when not recording. */
  level: number;
  start: () => Promise<void>;
  /** Stop and hand the clip to `onComplete`. */
  stop: () => void;
  /** Stop and DISCARD. Nothing is transcribed, nothing is uploaded. */
  cancel: () => void;
}

/** Whether this browser can record at all. */
export function isRecordingSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === "function"
  );
}

/** First supported container, or "" when none is (caller treats as unsupported). */
export function pickMimeType(): string {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }
  const supported = window.MediaRecorder.isTypeSupported;
  // Older WebKit ships MediaRecorder without isTypeSupported; assume the one
  // container it can actually produce rather than refusing to record.
  if (typeof supported !== "function") return "audio/mp4";
  for (const candidate of MIME_PREFERENCES) {
    if (supported(candidate)) return candidate;
  }
  return "";
}

export function useVoiceRecorder(
  options: UseVoiceRecorderOptions,
): UseVoiceRecorderResult {
  const { maxDurationMs, onComplete } = options;

  const [state, setState] = useState<RecorderState>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  /** Set by `cancel` so the `stop` handler discards instead of completing. */
  const discardRef = useRef(false);
  /** Latest callback without re-subscribing the recorder on every render. */
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  /** Release every OS-level resource. Safe to call repeatedly. */
  const teardown = useCallback((): void => {
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    // Stopping the tracks is what turns off the browser's recording
    // indicator. Leaving them live keeps the microphone hot after the user
    // thinks they stopped — the single worst bug this feature could ship.
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    analyserRef.current = null;
    if (audioCtxRef.current !== null) {
      void audioCtxRef.current.close().catch(() => undefined);
      audioCtxRef.current = null;
    }
    recorderRef.current = null;
    setLevel(0);
  }, []);

  useEffect(() => teardown, [teardown]);

  /** Attach an analyser for the level meter. Best-effort: never blocks recording. */
  const attachMeter = useCallback((stream: MediaStream): void => {
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (Ctor === undefined) return;
      const ctx = new Ctor();
      // iOS starts an AudioContext suspended until a user gesture; start()
      // is always called from a click, so resuming here is allowed.
      void ctx.resume().catch(() => undefined);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
    } catch {
      // A meter is decoration. If the audio graph is unavailable the
      // recording still has to work.
    }
  }, []);

  /** Current input level in [0,1], or 0 when no analyser is attached. */
  const readLevel = useCallback((): number => {
    const analyser = analyserRef.current;
    if (analyser === null) return 0;
    const bins = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(bins);
    let sum = 0;
    for (const bin of bins) sum += bin;
    const mean = sum / bins.length / 255;
    // Speech sits low in the normalized range; scale so the meter actually
    // moves at conversational volume instead of hugging the floor.
    return Math.min(1, mean * 2.5);
  }, []);

  const stopTracksAndFinish = useCallback((): void => {
    const chunks = chunksRef.current;
    const mimeType = recorderRef.current?.mimeType ?? pickMimeType();
    const durationMs = startedAtRef.current > 0 ? Date.now() - startedAtRef.current : 0;
    const discard = discardRef.current;

    teardown();
    setState("idle");
    setElapsedMs(0);
    chunksRef.current = [];

    if (discard || chunks.length === 0) return;
    onCompleteRef.current(new Blob(chunks, { type: mimeType }), mimeType, durationMs);
  }, [teardown]);

  const stop = useCallback((): void => {
    discardRef.current = false;
    const recorder = recorderRef.current;
    if (recorder !== null && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }
    // Never started, or already stopped — still release resources.
    stopTracksAndFinish();
  }, [stopTracksAndFinish]);

  const cancel = useCallback((): void => {
    discardRef.current = true;
    const recorder = recorderRef.current;
    if (recorder !== null && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }
    stopTracksAndFinish();
  }, [stopTracksAndFinish]);

  const start = useCallback(async (): Promise<void> => {
    if (!isRecordingSupported()) {
      setState("unsupported");
      return;
    }
    const mimeType = pickMimeType();
    if (mimeType === "") {
      setState("unsupported");
      return;
    }

    setState("requesting");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Every getUserMedia rejection lands here — denial, no device, or a
      // device already in use. They are indistinguishable in practice and
      // the user's next step is the same: check the browser's permissions.
      setState("denied");
      return;
    }

    discardRef.current = false;
    chunksRef.current = [];
    streamRef.current = stream;
    attachMeter(stream);

    const recorder = new MediaRecorder(stream, { mimeType });
    recorderRef.current = recorder;
    recorder.ondataavailable = (event: BlobEvent): void => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = stopTracksAndFinish;
    recorder.onerror = (): void => {
      // iOS fires this when a call arrives or the tab is backgrounded. Keep
      // whatever was captured rather than throwing the user's words away.
      discardRef.current = false;
      stopTracksAndFinish();
    };

    startedAtRef.current = Date.now();
    setElapsedMs(0);
    recorder.start();
    setState("recording");

    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      setLevel(readLevel());
      if (elapsed >= maxDurationMs) {
        // At the cap we STOP and transcribe. Discarding at the limit would
        // throw away a minute of someone's speech for hitting a number they
        // could not see coming.
        discardRef.current = false;
        const active = recorderRef.current;
        if (active !== null && active.state !== "inactive") active.stop();
      }
    }, TICK_MS);
  }, [attachMeter, maxDurationMs, readLevel, stopTracksAndFinish]);

  return { state, elapsedMs, level, start, stop, cancel };
}
